import { useState } from 'react';
import { RefreshCw, Sun, Moon, Settings, Menu, X, Loader2, LogOut } from 'lucide-react';
import type { Customer } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';
import kloudynetLogo from "@assets/image_1777160759976.png";
import { DASHBOARD_TIME_RANGES, dashboardTimeRangeLabel, type DashboardTimeRange } from '../lib/timeRange';

interface HeaderProps {
  darkMode: boolean;
  onToggleTheme: () => void;
  activeFilter: DashboardTimeRange;
  onFilterChange: (range: DashboardTimeRange) => void;
  lastRefresh: string;
  onRefresh: () => void;
  onOpenSettings: () => void;
  azureConfigured?: boolean;
  dataSource?: 'live' | 'loading' | 'empty';
  isLoading?: boolean;
  customers?: Customer[];
  activeCustomerId?: string;
  onCustomerChange?: (id: string) => void;
  activeCustomerName?: string;
  activeCustomerColor?: string;
  /** Hide the gear/Settings button (e.g. for read-only customer portal). */
  hideSettings?: boolean;
  /** Override the subtitle line under "SOC Dashboard". */
  subtitle?: string;
}

function hexToRgba(hex: string | undefined, alpha: number): string {
  const match = /^#?([0-9a-fA-F]{6})$/.exec(hex ?? '');
  if (!match) return `rgba(37, 99, 235, ${alpha})`;
  const raw = match[1];
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function Header({
  darkMode, onToggleTheme, activeFilter, onFilterChange, lastRefresh, onRefresh,
  onOpenSettings, azureConfigured, dataSource = 'empty', isLoading = false,
  customers = [], activeCustomerId, onCustomerChange, activeCustomerName, activeCustomerColor,
  hideSettings = false, subtitle,
}: HeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, signOut, roleBranding } = useAuth();
  const activeCustomer = activeCustomerId
    ? customers.find((customer) => customer.id === activeCustomerId)
    : customers.find((customer) => customer.name === activeCustomerName);
  const activeCustomerLogoUrl = activeCustomer?.logoUrl;
  const roleMeta = user?.role ? roleBranding[user.role] : undefined;
  const roleColor = roleMeta?.color || (user?.role === 'developer' ? '#f59e0b' : '#2563eb');
  const roleLabel = roleMeta?.label || user?.role || 'analyst';
  const filterBtns = DASHBOARD_TIME_RANGES;
  const hasMultiple = customers.length > 1;
  // Initials for the round avatar - derived from the signed-in user's name
  // (or email as a fallback). Caps at 2 chars so it fits the 7x7 circle.
  const initials = (user?.name || user?.email || '?')
    .split(/\s+|[@._-]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || 'U';

  return (
    <div className="bg-card rounded-xl shadow mb-3">
      <div className="flex justify-between items-center px-5 py-3.5">
        <div className="flex min-w-0 items-center gap-3 flex-wrap 2xl:flex-nowrap">
          <div className="flex items-center gap-3">
            <img
              src={kloudynetLogo}
              alt="Kloudynet"
              className="h-9 w-auto flex-shrink-0"
            />
            <div>
              <h1 className="text-xl font-bold text-foreground flex items-center gap-2 flex-wrap leading-tight">
                {dataSource === 'live' && (
                  <span className="pulse-live inline-block bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide">LIVE</span>
                )}
                {isLoading
                  ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-2 py-0.5 rounded-full">
                      <Loader2 size={10} className="animate-spin" /> Fetching
                    </span>
                  : azureConfigured
                    ? <span className="text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 px-2 py-0.5 rounded-full tracking-wide">AZURE</span>
                    : null
                }
              </h1>
              {subtitle && (
                <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
              )}
            </div>
          </div>

          {/* Customer switcher: dropdown only when there's actually a choice. */}
          {hasMultiple && onCustomerChange ? (
            <div className="flex items-center gap-1.5">
              {activeCustomerLogoUrl ? (
                <img src={activeCustomerLogoUrl} alt={`${activeCustomerName ?? 'Customer'} logo`} className="h-7 w-7 rounded-md object-contain bg-white border border-border p-0.5 shadow-sm" />
              ) : activeCustomerColor ? (
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: activeCustomerColor }} />
              ) : null}
              <select
                value={activeCustomerId ?? ''}
                onChange={e => onCustomerChange(e.target.value)}
                className="text-xs font-semibold border rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary transition-colors cursor-pointer bg-primary/10 border-primary/30 text-primary hover:bg-primary/20"
                title="Switch active customer"
              >
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
                {customers.length} tenants
              </span>
            </div>
          ) : customers.length === 1 && activeCustomerName ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted/50 border border-border">
              {activeCustomerLogoUrl ? (
                <img src={activeCustomerLogoUrl} alt={`${activeCustomerName ?? 'Customer'} logo`} className="h-6 w-6 rounded-md object-contain bg-white border border-border p-0.5" />
              ) : activeCustomerColor ? (
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: activeCustomerColor }} />
              ) : null}
              <span className="text-xs font-semibold text-foreground">{activeCustomerName}</span>
            </div>
          ) : null}

          <button
            className="md:hidden text-muted-foreground hover:text-foreground p-1"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <div className="hidden md:flex items-center gap-2">
          <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
            {filterBtns.map(d => (
              <button
                key={d}
                onClick={() => onFilterChange(d)}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                  activeFilter === d
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {dashboardTimeRangeLabel(d)}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Last refresh: <span className="text-foreground font-medium">{lastRefresh}</span></p>
          <button
            onClick={onRefresh}
            className="flex items-center gap-1.5 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-xs font-semibold hover:opacity-90 transition-opacity"
          >
            <RefreshCw size={13} strokeWidth={2.5} />
            Refresh
          </button>
          <button
            onClick={onToggleTheme}
            className="border border-border rounded-lg p-2 text-muted-foreground hover:border-primary hover:text-primary transition-all"
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? <Sun size={15} strokeWidth={2} /> : <Moon size={15} strokeWidth={2} />}
          </button>
          {!hideSettings && (
            <button
              onClick={onOpenSettings}
              className="border border-border rounded-lg p-2 text-muted-foreground hover:border-primary hover:text-primary transition-all"
              title="Settings"
            >
              <Settings size={15} strokeWidth={2} />
            </button>
          )}
          <div className="relative pl-1 border-l border-border">
            <button
              onClick={() => setUserMenuOpen((o) => !o)}
              onBlur={() => setTimeout(() => setUserMenuOpen(false), 150)}
              className="flex items-center gap-2 px-1.5 py-0.5 rounded-md hover:bg-muted/60 transition-colors"
              title={user?.email ?? ''}
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center border"
                style={{ backgroundColor: hexToRgba(roleColor, 0.16), borderColor: hexToRgba(roleColor, 0.35) }}
              >
                <span className="text-xs font-bold" style={{ color: roleColor }}>{initials}</span>
              </div>
              <div className="text-left hidden lg:block leading-tight">
                <div className="text-xs font-medium text-foreground truncate max-w-[140px]">{user?.name ?? 'Signed in'}</div>
                <div className="text-[10px] text-muted-foreground capitalize">{roleLabel}</div>
              </div>
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
                <div className="px-3 py-2 border-b border-border">
                  <div className="text-xs font-semibold text-foreground truncate">{user?.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
                  <div
                    className="mt-1 inline-block text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border"
                    style={{ backgroundColor: hexToRgba(roleColor, 0.12), borderColor: hexToRgba(roleColor, 0.28), color: roleColor }}
                  >
                    {roleLabel}
                  </div>
                </div>
                <button
                  onMouseDown={(e) => { e.preventDefault(); signOut(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-muted transition-colors"
                >
                  <LogOut size={13} /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden flex flex-col gap-3 p-4 border-t border-border">
          {hasMultiple && onCustomerChange ? (
            <div className="flex items-center gap-2">
              {activeCustomerLogoUrl ? (
                <img src={activeCustomerLogoUrl} alt={`${activeCustomerName ?? 'Customer'} logo`} className="h-8 w-8 rounded-md object-contain bg-white border border-border p-0.5" />
              ) : null}
            <select
              value={activeCustomerId ?? ''}
              onChange={e => onCustomerChange(e.target.value)}
              className="w-full text-sm border border-border bg-background rounded-lg px-3 py-2 text-foreground focus:outline-none"
            >
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            </div>
          ) : customers.length === 1 && activeCustomerName ? (
            <div className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border border-border">
              {activeCustomerLogoUrl ? (
                <img src={activeCustomerLogoUrl} alt={`${activeCustomerName ?? 'Customer'} logo`} className="h-7 w-7 rounded-md object-contain bg-white border border-border p-0.5" />
              ) : activeCustomerColor ? (
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: activeCustomerColor }} />
              ) : null}
              <span className="text-sm font-semibold text-foreground">{activeCustomerName}</span>
            </div>
          ) : null}
          <div className="flex gap-1 flex-wrap">
            {filterBtns.map(d => (
              <button key={d} onClick={() => { onFilterChange(d); setMobileOpen(false); }}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-all ${activeFilter === d ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground'}`}
              >
                {dashboardTimeRangeLabel(d)}
              </button>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={onRefresh} className="flex items-center justify-center gap-1.5 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-semibold flex-1">
              <RefreshCw size={13} /> Refresh
            </button>
            <button onClick={onToggleTheme} className="border border-border rounded-lg p-2">
              {darkMode ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {!hideSettings && (
              <button onClick={onOpenSettings} className="border border-border rounded-lg p-2">
                <Settings size={16} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
